import { getImageSrc, isDocumentImage } from '@btime/helpers'

import { QUESTIONS, createReader, unique } from './documentData'
import { CERTIFICATE_QUESTIONS, METHODS } from './certificateData'
import { findDosage, joinHolds } from './gasFreeData'
import { GOODS_EN, GUIDE_QUESTIONS, sortNumerically, translate } from './guideData'
import { toDays } from './noticeData'

export const PHOTOS_PER_PAGE = 6

// Perguntas usadas só no Relatório Fotográfico
const REPORT_QUESTIONS = {
  holds: 196, // filha Lineup (seleção múltipla, opções "1" a "9")
  // filha "Realização do Serviço: Fumigação"
  start: 556, // "Início do Serviço"
  end: 557, // "Término do Serviço"
  temperature: 560,
  // filha do questionário "Fotos para Relatório Fotográfico" (serviço 18), só galerias de fotos
  shipPhotos: 1009 // "Foto do nome do Navio"
}

// Galerias do porão N (1 a 9) no questionário de fotos: cada porão ocupa 5 perguntas seguidas, nesta ordem:
// "Fotos antes da aplicação", "Fotos durante a aplicação", "Fotos após a aplicação", "Houve Recirculação?" (seleção,
// sem foto) e "Fotos da Recirculação"
const FIRST_HOLD_PHOTO_QUESTION = 1011
const QUESTIONS_PER_HOLD = 5
const PHOTO_OFFSETS = [0, 1, 2, 4]
const holdPhotoQuestions = hold => {
  const first = FIRST_HOLD_PHOTO_QUESTION + (hold - 1) * QUESTIONS_PER_HOLD
  return PHOTO_OFFSETS.map(offset => first + offset)
}

const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE'].concat([
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER'
])

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp']
const extensionOf = src => (typeof src === 'string' ? src.split('?')[0].split('.').pop().toLowerCase() : '')

const pad = number => String(number).padStart(2, '0')

// 1 -> "1ST", 2 -> "2ND", 3 -> "3RD", 11 -> "11TH", 26 -> "26TH"
const ordinal = day => {
  const suffix = day % 100 >= 11 && day % 100 <= 13 ? 'TH' : { 1: 'ST', 2: 'ND', 3: 'RD' }[day % 10] || 'TH'
  return `${day}${suffix}`
}

// "2026-08-26T20:55:00Z" -> "AUGUST 26TH, 2026 AT 05:55 PM" (vazio se não houver data)
export const formatDateTimeEn = value => {
  const date = new Date(value)
  if (!value || Number.isNaN(date.getTime())) return ''
  const hours = date.getHours()
  const time = `${pad(hours % 12 || 12)}:${pad(date.getMinutes())} ${hours < 12 ? 'AM' : 'PM'}`
  return `${MONTHS[date.getMonth()]} ${ordinal(date.getDate())}, ${date.getFullYear()} AT ${time}`
}

// 61226.73 -> "61,226.730"
export const formatNumberEn = (value, decimals = 3) => {
  const number = Number(value)
  return value === '' || Number.isNaN(number)
    ? ''
    : number.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

// "Paranaguá" -> "PARANAGUA, BRAZIL" (sem acentos e sem o " / UF" que alguns locais trazem no nome)
export const formatPort = name => {
  const city = name.split('/')[0].normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase()
  return city && `${city}, BRAZIL`
}

const plural = (count, word) => `${count} ${word}${Number(count) === 1 ? '' : 'S'}`

// "2,0" -> "2.0 GR/ M3"; "2,5" -> "2.5 GR/ M3"
const formatDosageEn = dosage => dosage && `${dosage.includes(',') ? dosage.replace(',', '.') : `${dosage}.0`} GR/ M3`

// Os itens da galeria podem trazer os dados do arquivo dentro de `document` (mesmo tratamento do Gallery dos PDFs)
const isImageItem = item => isDocumentImage(item) || IMAGE_EXTENSIONS.includes(extensionOf(getImageSrc(item)))
const imageSources = value =>
  (Array.isArray(value) ? value : [])
    .filter(Boolean)
    .map(item => ({ ...(item.document || {}), ...item }))
    .filter(isImageItem)
    .map(getImageSrc)
    .filter(Boolean)

export const buildPhotoReportData = ({ serviceOrder, childrenServiceOrders = [], assets = [] }) => {
  const { answers, answer } = createReader({ serviceOrder, childrenServiceOrders, assets })
  const replies = [serviceOrder, ...childrenServiceOrders].flatMap(so => so.replies || [])
  // fotos de uma pergunta de galeria, na ordem em que foram anexadas
  const photosOf = questionId =>
    replies.filter(reply => reply.questionId === questionId).flatMap(reply => imageSources(reply.value))

  const goods = answer(QUESTIONS.goods).toUpperCase()
  const port = formatPort(answer(QUESTIONS.mapaPort))
  const total = formatNumberEn(answer(QUESTIONS.quantity))
  const method = (CERTIFICATE_QUESTIONS.methods.map(answer).find(Boolean) || '').toUpperCase()
  const hours = GUIDE_QUESTIONS.exposureTimes.map(answer).find(Boolean) || ''
  const days = toDays(Number(hours))
  const temperature = answer(REPORT_QUESTIONS.temperature)
  const holds = sortNumerically(answer(REPORT_QUESTIONS.holds)).split(', ').filter(Boolean)
  // não há quantidade por exportador: com um só exportador vale o total; com vários, a coluna fica em branco
  const shippers = unique(answers(QUESTIONS.exporter).map(name => name.toUpperCase())).map((name, _, list) => ({
    name,
    quantity: list.length === 1 ? total : ''
  }))

  const photos = [
    ...photosOf(REPORT_QUESTIONS.shipPhotos),
    ...holds.filter(hold => hold >= 1 && hold <= 9).flatMap(hold => holdPhotoQuestions(Number(hold)).flatMap(photosOf))
  ]
  const photoPages = []
  for (let index = 0; index < photos.length; index += PHOTOS_PER_PAGE) {
    photoPages.push(photos.slice(index, index + PHOTOS_PER_PAGE))
  }

  return {
    vessel: answer(QUESTIONS.ship).toUpperCase(),
    goods: translate(GOODS_EN, goods),
    total: total && `${total} MT`,
    portOfLoading: port,
    portOfDischarge: answer(QUESTIONS.destination).toUpperCase(),
    start: formatDateTimeEn(answer(REPORT_QUESTIONS.start)),
    end: formatDateTimeEn(answer(REPORT_QUESTIONS.end)),
    localOfTreatment: port,
    method: (METHODS[method] || [method])[0],
    dosage: formatDosageEn(findDosage(answer)),
    temperature: temperature && `${temperature}°C`,
    exposureTime: hours && `${plural(days, 'DAY')}/ ${plural(hours, 'HOUR')}`,
    holdsTreated: joinHolds(holds, 'AND'),
    shippers: shippers.length ? shippers : [{ name: '', quantity: '' }],
    shippersTotal: total && `${total} MT`,
    photoPages
  }
}
